import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Users, Download } from "lucide-react";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 10;

// helper to render all program names (comma-separated)
const programsLabel = (s) => {
  const list = s.programs && s.programs.length ? s.programs : (s.program ? [s.program] : []);
  if (!list.length) return "-";
  return list.map((p) => p.name).join(" + ");
};

function StatusBadge({ status }) {
  return (
    <Badge variant="outline" className={`status-${status} font-semibold`} data-testid={`student-status-${status}`}>
      {status}
    </Badge>
  );
}

function FeeBadge({ status }) {
  const labels = { paid: "Paid", partial: "Partial", overdue: "Overdue", due: "Due" };
  return (
    <Badge variant="outline" className={`status-${status} font-semibold`}>
      {labels[status] || status}
    </Badge>
  );
}

export default function Students() {
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [programId, setProgramId] = useState("all");
  const [feeStatus, setFeeStatus] = useState("all");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      if (programId !== "all") params.program_id = programId;
      if (feeStatus !== "all") params.fee_status = feeStatus;
      const [s, p] = await Promise.all([
        api.get("/students", { params }),
        api.get("/programs"),
      ]);
      setStudents(s.data);
      setPrograms(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setPage(1); }, [status, programId, feeStatus]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const exportCsv = () => {
    const rows = [["Student ID", "Name", "Phone", "Program", "Status", "Fee Status", "Total", "Paid", "Outstanding"]];
    students.forEach((s) => {
      rows.push([
        s.student_id, s.name, s.phone, programsLabel(s),
        s.status, s.fee_summary.status, s.fee_summary.total, s.fee_summary.paid, s.fee_summary.outstanding,
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `students-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px] mx-auto" data-testid="students-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2"><Users size={12} /> Admissions</div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Students</h1>
          <p className="text-slate-500 mt-1">{students.length} students found</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-button">
            <Download size={16} className="mr-2" /> Export
          </Button>
          <Button
            className="bg-slate-900 hover:bg-slate-800"
            onClick={() => navigate("/students/new")}
            data-testid="add-student-button"
          >
            <Plus size={16} className="mr-2" /> Add Student
          </Button>
        </div>
      </div>

      <Card className="p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="student-search-input"
              placeholder="Search by name, phone, or student ID"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full md:w-44 h-11" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="inquiry">Inquiry</SelectItem>
              <SelectItem value="enrolled">Enrolled</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="dropped">Dropped</SelectItem>
            </SelectContent>
          </Select>
          <Select value={programId} onValueChange={setProgramId}>
            <SelectTrigger className="w-full md:w-48 h-11" data-testid="filter-program">
              <SelectValue placeholder="Program" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {programs.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={feeStatus} onValueChange={setFeeStatus}>
            <SelectTrigger className="w-full md:w-40 h-11" data-testid="filter-fee-status">
              <SelectValue placeholder="Fee Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All fees</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="due">Due</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800 h-11" data-testid="search-button">
            Search
          </Button>
        </form>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps">Student ID</th>
                <th className="px-4 py-3 label-caps">Name</th>
                <th className="px-4 py-3 label-caps">Phone</th>
                <th className="px-4 py-3 label-caps">Program</th>
                <th className="px-4 py-3 label-caps">Status</th>
                <th className="px-4 py-3 label-caps">Fees</th>
                <th className="px-4 py-3 label-caps text-right">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && students.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-500">No students found</td></tr>
              )}
              {students.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/students/${s.id}`)}
                  data-testid={`student-row-${s.id}`}
                >
                  <td className="px-4 py-4 font-mono text-xs text-slate-600">{s.student_id}</td>
                  <td className="px-4 py-4 font-medium">{s.name}</td>
                  <td className="px-4 py-4 font-mono text-xs">{s.phone}</td>
                  <td className="px-4 py-4">{programsLabel(s)}</td>
                  <td className="px-4 py-4"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-4"><FeeBadge status={s.fee_summary.status} /></td>
                  <td className="px-4 py-4 text-right font-semibold">{formatINR(s.fee_summary.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={PAGE_SIZE} total={students.length} onPageChange={setPage} testIdPrefix="students-pagination" />
      </Card>
    </div>
  );
}
